import React, { useContext, useState } from 'react';
import { Link, Modal, ResponsiveImage, SystemFeaturesContext, UserContext } from '@/components';
import { ScrapbookContentType, TechLaunchDatabaseModel } from '@/types';
import DownloadIcon from '@/assets/downloadSimpleLight.svg';
import ScissorIcon from '@/assets/scissors-light.svg';
import ReportButton from '@/components/common/ReportButton';
import { useTranslations } from 'next-intl';
import { SaveToScrapbookButton } from '@/components/scrapbook';
import TechLaunchDatabaseContent from './TechLaunchDatabaseContent';
import ImageIcon from '@/assets/imageIcon.svg';
import XLight from '@/assets/xLight.svg';
import ArrowRight from '@/assets/arrowRightLight.svg';
import dayjs from 'dayjs';

type TechLaunchDatabaseSearchItemModel = {
  data: TechLaunchDatabaseModel;
};

const TechLaunchDatabaseSearchItem = ({ data }: TechLaunchDatabaseSearchItemModel) => {
  const [open, setOpen] = useState(false);

  const user = useContext(UserContext);
  const { saveToScrapbook: systemFeaturesSaveToScrapbook } = useContext(SystemFeaturesContext);

  const t = useTranslations();

  const closeDialog = () => {
    setOpen(false);
  };

  const download = () => {
    // TODO: implement download logic
  };

  return (
    <>
      <div className="group relative h-full w-full overflow-hidden rounded-md">
        <div
          className="flex h-full w-full cursor-pointer flex-col border border-[#f4f5f7] bg-[#f4f5f7]"
          onClick={() => setOpen(true)}
        >
          <div className="relative aspect-[16/8] overflow-hidden">
            {data.loading ? (
              <div className="flex h-full w-full animate-pulse items-center justify-center bg-gray-300">
                <ImageIcon className="h-10 w-10 text-gray-200 dark:text-gray-600" />
              </div>
            ) : (
              data.innovationImages && (
                <ResponsiveImage
                  image={data.innovationImages}
                  widths={{ xs: 400, sm: 585, md: 370, lg: 312, xl: 380, xxl: 340 }}
                  heights={{ xs: 200, sm: 300, md: 190, lg: 160, xl: 190, xxl: 170 }}
                  dataNotLazy
                  preload
                />
              )
            )}
          </div>
          <div className="flex flex-1 flex-col gap-2 p-3">
            {data.loading ? (
              <>
                <div className="h-5.5 w-1/4 animate-pulse rounded-full bg-gray-200"></div>
                <div className="mb-0.5">
                  <div className="mb-1 h-6 w-full animate-pulse rounded-full bg-gray-200"></div>
                  <div className="h-6 w-2/3 animate-pulse rounded-full bg-gray-200"></div>
                </div>
                <div className="border-b border-gray-300" />
                <div className="flex flex-col gap-1">
                  <div className="h-5.5 w-1/4 animate-pulse rounded-full bg-gray-200"></div>
                  <div className="h-5.5 w-full animate-pulse rounded-full bg-gray-200"></div>
                  <div className="h-5.5 w-full animate-pulse rounded-full bg-gray-200"></div>
                </div>
              </>
            ) : (
              <>
                <div className="flex flex-col gap-2">
                  <div className="text-sm font-bold">
                    {data.publishedDate ? dayjs(data.publishedDate).format('MMMM YYYY') : ''}
                  </div>
                  <h3 className="line-clamp-2 min-h-14 text-xl">{data.title}</h3>
                  <div className="border-b border-gray-300" />
                </div>
                <div>
                  <div className="text-sm font-light">Industry</div>
                  <div className="line-clamp-2">{data.industry?.join(', ')}</div>
                </div>
              </>
            )}
          </div>
        </div>
        {(user.isAuthenticated || user.isIpBasedIdentity) && !data.loading && (
          <div className="buttons pointer-events-none absolute top-0 hidden w-full justify-end opacity-0 transition-opacity duration-500 group-hover:opacity-100 md:flex pointer-coarse:opacity-100">
            <div className="button-list mt-2 mr-2">
              {/*
              <div className="flex justify-end">
                <ReportButton
                  icon={DownloadIcon}
                  text={t('general.$download')}
                  textClassName="pr-7"
                  onClick={download}
                />
              </div>
              */}
              {!user.isIpBasedIdentity && systemFeaturesSaveToScrapbook && (
                <div className="flex justify-end">
                  <SaveToScrapbookButton
                    scrapbookContent={{ ...data }}
                    scrapbookContentType={ScrapbookContentType.TechLaunchDatabase}
                    scrapbookPath={`tech-launch-database.${data.innovationId}.json`}
                    successMessage="Tech launch saved to scrapbook successfully"
                    tooltipProps={{ inactive: true, content: ' ' }}
                    className="cursor-pointer"
                  >
                    <ReportButton icon={ScissorIcon} text={t('general.$save')} textClassName="pr-10" />
                  </SaveToScrapbookButton>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <Modal open={open} onClose={closeDialog} contanerClassName="w-[90vw] xl:w-[70vw] h-[90vh] bg-white shadow-xl">
        <div className="flex h-full flex-col">
          <div className="flex items-start justify-between border-b border-gray-300 p-4 pr-3 lg:pr-4 lg:pl-10">
            <div className="flex h-full items-center">
              <h2 className="text-xl font-medium lg:text-3xl">{data.title}</h2>
            </div>
            <button onClick={closeDialog} className="btn icon">
              <XLight className="h-7 w-7" />
            </button>
          </div>
          <div className="flex-1 overflow-y-scroll p-4 lg:px-10" data-lenis-prevent>
            <TechLaunchDatabaseContent data={data} />
          </div>
          <div className="v-card__actions flex-shrink-0 justify-between rounded-b-lg bg-gray-100 py-5">
            <div className="spacer flex-1"></div>
            {data.link && (
              <Link
                link={data.link}
                className="v-btn view-btn float-right mr-[19px] flex min-h-[44px] items-center rounded-4xl border border-black/20 px-7 py-2 !font-semibold tracking-[1.25px] !text-black/80 hover:bg-black/5"
              >
                <div>View Details</div>
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default TechLaunchDatabaseSearchItem;
